import React from 'react'
import { Button, Modal } from 'react-bootstrap'
import { ErrorMessage, Field, Form, Formik } from 'formik'
import * as yup from 'yup'
import Select from 'react-select/creatable'
import { shallowEqual, useSelector } from 'react-redux'
import { getSelectedVolume } from '../../entity/volumes/Volumes'

const CreateArticleModal = ({ isOpen, onClose, data, onSubmit }) => {
  const volumeData = useSelector(getSelectedVolume, shallowEqual)

  const validationSchema = yup.object().shape({
    field: yup.string().required('სამეცნიერო დარგი აუცილებელია'),
    field_ka: yup.string().required('სამეცნიერო დარგი ქართულად აუცილებელია'),
    section: yup.string().required('სექცია აუცილებელია'),
    section_ka: yup.string().required('სექცია აუცილებელია'),
    title: yup.string().required('დასახელება აუცილებელია'),
    title_ka: yup.string().required('დასახელება ქართულად აუცილებელია'),
    authors: yup.array(),
    authors_ka: yup.array(),
    abstract: yup.string().required('აბსტრაქტი აუცილებელია'),
    abstract_ka: yup.string().required('აბსტრაქტი ქართულად აუცილებელია'),
    keywords: yup.array(),
    keywords_ka: yup.array(),
    doi_url: yup.string(),
    pdf_url: yup.mixed().required('არტიკლი დამატება აუცილებელია'),
    volumeYear: yup.string(),
    issueNumber: yup.string(),
  })

  const initialValues = {
    field: data?.field ?? '',
    field_ka: data?.field_ka ?? '',
    section: data?.section ?? '',
    section_ka: data?.section_ka ?? '',
    title: data?.title ?? '',
    title_ka: data?.title_ka ?? '',
    authors: data?.authors || [],
    authors_ka: data?.authors_ka ?? [],
    abstract: data?.abstract ?? '',
    abstract_ka: data?.abstract_ka ?? '',
    keywords: data?.keywords ?? [],
    keywords_ka: data?.keywords_ka ?? [],
    doi_url: data?.doi_url ?? '',
    volumeYear: data?.volumeYear || volumeData?.volume?.year,
    issueNumber: data?.issueNumber || volumeData?.issue?.number,
    pdf_url: data?.pdf_url || null,
  }

  const labelMapping = {
    field: 'სამეცნიერო დარგი (Eng)',
    field_ka: 'სამეცნიერო დარგი (ქართულად)',
    section: 'სექცია (Eng)',
    section_ka: 'სექცია (ქართულად)',
    title: 'დასახელება (Eng)',
    title_ka: 'დასახელება (ქართულად)',
    authors: 'ავტორები (Eng)',
    authors_ka: 'ავტორები (ქართულად)',
    abstract: 'რეზიუმე (Eng)',
    abstract_ka: 'რეზიუმე (ქართულად)',
    keywords: 'საკვანძო სიტყვები (Eng)',
    keywords_ka: 'საკვანძო სიტყვები (ქართულად)',
    doi_url: 'DOI URL',
    pdf_url: 'არტიკლი',
    volumeYear: 'წელი',
    issueNumber: 'ნომერი',
  }

  const renderInput = (key, formik) => {
    if (key === 'abstract' || key === 'abstract_ka') {
      return (
        <Field name={key}>
          {({ field }) => (
            <textarea
              className='border rounded p-2'
              id={key}
              {...field}
              placeholder={labelMapping[key]}
              rows={4}
            />
          )}
        </Field>
      )
    } else if (
      key === 'keywords' ||
      key === 'keywords_ka' ||
      key === 'authors' ||
      key === 'authors_ka'
    ) {
      return (
        <Select
          isMulti
          isClearable
          name={key}
          className='basic-multi-select'
          classNamePrefix='select'
          value={formik.values[key].map(option => ({ value: option, label: option }))}
          onChange={selectedOptions => {
            const values = selectedOptions ? selectedOptions.map(option => option.value) : []
            formik.setFieldValue(key, values)
          }}
        />
      )
    } else if (key === 'pdf_url') {
      return (
        <input
          type='file'
          id={key}
          onChange={event => formik.setFieldValue(key, event.currentTarget.files[0])}
          name='pdfFile'
        />
      )
    } else {
      return (
        <Field
          className='border rounded py-2'
          autoComplete='off'
          type='text'
          id={key}
          name={key}
          placeholder={labelMapping[key]}
        />
      )
    }
  }

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      size='lg'
      aria-labelledby='contained-modal-title-vcenter'
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title id='contained-modal-title-vcenter'>არტიკლის დამატება</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={values => {
            const formData = new FormData()
            Object.keys(values).forEach(key => {
              if (key === 'pdf_url') {
                formData.append('pdfFile', values[key])
              } else if (Array.isArray(values[key])) {
                formData.append(key, JSON.stringify(values[key]))
              } else if (key === 'volumeYear') {
                formData.append('volumeYear', values[key])
              } else if (key === 'issueNumber') {
                formData.append('issueNumber', values[key])
              } else {
                formData.append(key, values[key])
              }
            })
            onSubmit(formData)
          }}
        >
          {formik => (
            <Form encType='multipart/form-data'>
              <div className='flex flex-col gap-4 p-3.5'>
                {Object.keys(initialValues).map(
                  key =>
                    key !== 'volumeYear' &&
                    key !== 'issueNumber' && (
                      <span key={key} className='flex flex-col'>
                        <label htmlFor={key} className='text-lg'>
                          {labelMapping[key] && labelMapping[key]}
                        </label>
                        {key !== 'volumeYear' && key !== 'issueNumber' && renderInput(key, formik)}
                        <ErrorMessage style={{ color: 'red' }} name={key} component='span' />
                      </span>
                    ),
                )}
                <div className='flex flex-row items-center gap-3 justify-end w-full'>
                  <Button variant='primary' type='submit'>
                    {data ? 'შენახვა' : 'დამატება'}
                  </Button>
                  <Button onClick={onClose}>Cancel</Button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  )
}

export default CreateArticleModal
